import {FormConfig} from '../../../models/form-config';
import {FieldType} from '../../../models/field-type';
import {AgreementModel} from '../../../../core/models/agreement.model';
import {FieldConfig} from '../../../models/field-config';
import {
    PredefinedValuesService
} from '../../../../core/services/impl/section-services/predefined-values/predefined-values.service';
import {AgreementsService} from '../../../services/agreements.service';
import {OwnerDataConsts} from '../../../../core/consts/owner-data.consts';
import {Validator} from '../../../models/validator.enum';
import {UrlParamsService} from '../../../../core/services/impl/url-params.service';
import {ApplicationContextService} from '../../../../core/services/impl/configuration/application-context.service';
import {isNext} from '../../../../core/utils/utils';
import {RegExpKey, RegExpService} from '../../../services/reg-exp.service';

export class ContactDataSectionConfig {

    language: string;
    isNext = isNext();

    constructor(private predefined: PredefinedValuesService,
                private agreementsService: AgreementsService,
                private contextService: ApplicationContextService,
                private urlParamsService: UrlParamsService,
                private regExpService: RegExpService) {
    }

    createConfig(language: string): FormConfig {
        this.language = language.toUpperCase();

        const formConfig = <FormConfig>{
            formKey: 'CONTACT_SECTION',
            fieldsConfig: [
                {
                    fieldKey: OwnerDataConsts.EMAIL,
                    caption: 'OWNER_DATA_SECTION.FORM_FIELDS.email',
                    type: FieldType.TextField,
                    personalData: true,
                    displayInPreviewMode: true,
                    displayInEditableMode: true,
                    validators: this.getEmailValidators(),
                    autocomplete: 'email',
                    properties: {
                        textTransform: {
                            type: 'lowercase'
                        },
                    }
                },
            ]
        };

        if (this.contextService.calculationPhoneNumberVisible) {
            formConfig.fieldsConfig.push({
                fieldKey: OwnerDataConsts.PHONE_NUMBER,
                caption: 'OWNER_DATA_SECTION.FORM_FIELDS.phoneNumber',
                personalData: true,
                displayInPreviewMode: true,
                displayInEditableMode: true,
                type: FieldType.TextField,
                validators: this.getPhoneNumberValidators(),
                autocomplete: 'tel',
            });
        }

        formConfig.fieldsConfig.forEach(fieldConfig => {
            this.predefined.addPredefinedValue(
                fieldConfig,
                this.predefined.values,
                formConfig.formKey,
                fieldConfig.fieldKey
            )
        });

        return formConfig
    }

    createAgreementConfig(agreement: AgreementModel): FieldConfig {
        const agreementShortValue = this.getAgreementValue(agreement).shortValue;
        const agreementValue = agreementShortValue ? agreementShortValue : this.getAgreementValue(agreement).value;
        return {
            fieldKey: agreement.code,
            fieldsDataFormat: 'objectArray',
            captionAsHTML: agreement.details.required ? `${this.agreementsService.REQUIRED_SYMBOL} ${agreementValue}` : agreementValue,
            type: FieldType.Checkbox,
            predefined: {
                isPredefined: false,
                additionalInfo: 'This is additional information email'
            },
            validators: agreement.details.required ? ['requiredTrue'] : [],
            beanConfig: {
                propertyName: 'agreements',
                keyPropertyName: 'code',
            },
            checkboxConfig: {
                readonly: false,
                expand: this.getCheckboxConfigExpand(agreement),
                mappingConfig: {
                    propertyName: 'selectedAnswer',
                    valuesMapping: this.agreementsService.getValuesMapping(agreement)
                }
            },
        };
    }

    private getCheckboxConfigExpand(agreement: AgreementModel): any {
        if (!!this.getAgreementValue(agreement).shortValue) {
            const expandText = this.getAgreementValue(agreement).value;
            return {
                expandedLabel: this.isNext ? 'expand' : 'SHARED.AGREEMENTS.showAgreementFullText',
                collapsedLabel: this.isNext ? 'collapse' : 'SHARED.AGREEMENTS.hideAgreementFullText',
                expandText: agreement.details.required ? `${this.agreementsService.REQUIRED_SYMBOL} ${expandText}` : expandText,
                inline: this.isNext
            };
        }
        return null;
    }

    private getAgreementValue(agreement: any): any {
        const agreementValues = agreement.details.values.filter(item => item.languageCode.toUpperCase() === this.language);
        if (agreementValues.length > 0) {
            return agreementValues[0];
        } else {
            throw new Error(`Agreement does not contain value for given language code: ${this.language}.`);
        }
    }

    private getEmailValidators(): any[] {
        const validators: any[] = [
            {
                name: Validator.PATTERN_EMAIL,
                patternEmail: '^[a-z\\d]+[\\w\\d.-]*@(?:[a-z\\d]+[a-z\\d-]+\\.){1,5}[a-z]{2,6}$'
            },
            {
                name: Validator.MAX_LENGTH,
                maxLength: 250
            },
            {
                name: Validator.MIN_LENGTH,
                minLength: 5
            }
        ];

        if (!this.urlParamsService.isAutomatOrigin && this.contextService.calculationEmailRequired) {
            validators.push(Validator.REQUIRED);
        }

        return validators;
    }

    private getPhoneNumberValidators(): any[] {
        const validators: any[] = [
            Validator.PHONE_NUMBER_ZEROES,
            {
                name: Validator.PATTERN,
                pattern: this.regExpService.getRegExp(RegExpKey.PHONE_NUMBER)
            },
            {
                name: Validator.MAX_LENGTH,
                maxLength: 11
            },
            {
                name: Validator.MIN_LENGTH,
                minLength: 2
            }
        ];

        if (this.contextService.calculationPhoneNumberVisible && this.contextService.calculationPhoneNumberRequired) {
            validators.push(Validator.PHONE_NUMBER_REQUIRED);
        }

        return validators;
    }
}
